import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './WelcomePage.css';

const ActivationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, activate, resendActivation } =
    useAuth();

  const token = searchParams.get('token');

  const [activationState, setActivationState] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);

  // Keep track of latest authentication state for the redirection timeout closure
  const isAuthRef = React.useRef(isAuthenticated);
  useEffect(() => {
    isAuthRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Keep track of whether activation has been triggered to prevent duplicate calls
  const hasTriggeredRef = React.useRef(false);

  // Redirect to login if not authenticated and no token is present
  useEffect(() => {
    if (!token && !isAuthenticated && !isLoading) {
      navigate('/login');
    }
  }, [token, isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (token && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      const performActivation = async () => {
        setActivationState('loading');
        try {
          await activate(token);
          setActivationState('success');
          // Wait 3 seconds, then navigate to welcome page (if logged in) or login page (if not)
          setTimeout(() => {
            if (isAuthRef.current) {
              navigate('/welcome');
            } else {
              navigate('/login');
            }
          }, 3000);
        } catch (err: any) {
          setActivationState('error');
          setErrorMsg(
            err?.message ||
              'Dieser Aktivierungslink ist ungültig oder wurde bereits verwendet.',
          );
        }
      };
      performActivation();
    }
  }, [token, activate, navigate]);

  const handleResend = async () => {
    setIsResending(true);
    try {
      await resendActivation();
    } catch {
      // Error logging/toast is already handled in AuthContext
    } finally {
      setIsResending(false);
    }
  };

  if (activationState === 'loading') {
    return (
      <div className="welcome-page">
        <div className="welcome-container">
          <div className="activation-spinner"></div>
          <h1 className="welcome-title">Konto wird aktiviert...</h1>
          <p style={{ color: '#a7a3e2', fontSize: '18px', margin: 0 }}>
            Bitte warte einen Moment, während wir deine E-Mail-Adresse
            verifizieren.
          </p>
        </div>
      </div>
    );
  }

  if (activationState === 'success') {
    return (
      <div className="welcome-page">
        <div className="welcome-container">
          <div className="activation-success-icon">✓</div>
          <h1 className="welcome-title">Erfolgreich aktiviert!</h1>
          <p style={{ color: '#a7a3e2', fontSize: '18px', margin: 0 }}>
            Dein Account wurde erfolgreich aktiviert. Du wirst in Kürze zur
            {isAuthenticated ? ' Willkommensseite' : ' Login-Seite'}{' '}
            weitergeleitet...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="welcome-page">
      <div className="welcome-container">
        <h1 className="welcome-title">
          {activationState === 'error'
            ? 'Aktivierung fehlgeschlagen'
            : 'Bitte bestätige deine E-Mail-Adresse!'}
        </h1>

        {activationState === 'error' ? (
          <div className="activation-error-box">{errorMsg}</div>
        ) : (
          <p style={{ color: '#a7a3e2', fontSize: '18px', margin: 0 }}>
            Wir haben dir einen Bestätigungslink gesendet
            {user?.email ? ` an ${user.email}` : ''}. Bitte klicke auf den Link
            in der E-Mail, um dein Konto zu aktivieren.
          </p>
        )}

        <button
          className="welcome-button"
          onClick={handleResend}
          disabled={isResending}
        >
          <span className="welcome-button-text">
            {isResending ? 'Wird gesendet...' : 'E-Mail erneut senden'}
          </span>
        </button>
      </div>
    </div>
  );
};

export default ActivationPage;
